import React, { useMemo } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import styles from '../../InpatientPortal.module.css';
import { useSelector } from 'react-redux';
import { formatValueByModalityId, getVitalUnit } from '../../../../utils/unitUtils';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler
);


const InpatientBloodPressureChart = ({ vitalConfig, measurements }) => {
    const {dateFormat, unitType} = useSelector((state) => state.userInfo);

    // Filter measurements for systolic (modalityId 5) and diastolic (modalityId 10)
    const systolicMeasurements = useMemo(() => {
        if (!measurements) return [];
        return measurements.filter(measurement => measurement.modalityId === 5);
    }, [measurements]);

    const diastolicMeasurements = useMemo(() => {
        if (!measurements) return [];
        return measurements.filter(measurement => measurement.modalityId === 10);
    }, [measurements]);
    
    // Calculate values before hooks - safe even if vitalConfig is null
    const modalityId = vitalConfig?.modalityId || null;
    
    // Get BP limits from vitalConfig (assuming it has systolic/diastolic limits)
    const systolicUpperLimit = vitalConfig?.systolicUpperLimit || vitalConfig?.upperLimit || null;
    const systolicLowerLimit = vitalConfig?.systolicLowerLimit || vitalConfig?.lowerLimit || null;
    const diastolicUpperLimit = vitalConfig?.diastolicUpperLimit || null;
    const diastolicLowerLimit = vitalConfig?.diastolicLowerLimit || null;

    // Get highest and lowest measurements from actual data
    const systolicValues = useMemo(() => {
        return systolicMeasurements
            .map(m => m.measurement)
            .filter(val => val !== null && val !== undefined && !isNaN(val));
    }, [systolicMeasurements]);

    const diastolicValues = useMemo(() => {
        return diastolicMeasurements
            .map(m => m.measurement)
            .filter(val => val !== null && val !== undefined && !isNaN(val));
    }, [diastolicMeasurements]);
    
    const highestSystolic = systolicValues.length > 0 ? Math.max(...systolicValues) : null;
    const lowestDiastolic = diastolicValues.length > 0 ? Math.min(...diastolicValues) : null;
    const highestDiastolic = diastolicValues.length > 0 ? Math.max(...diastolicValues) : null;
    const lowestSystolic = systolicValues.length > 0 ? Math.min(...systolicValues) : null;

    // Calculate domain using same logic as BloodPressureCardView
    const domain = useMemo(() => {
        // Start with the limits
        let minDomain = diastolicLowerLimit || systolicLowerLimit || null;
        let maxDomain = systolicUpperLimit || diastolicUpperLimit || null;

        // Adjust domain based on actual data
        if (lowestDiastolic !== null) {
            if (minDomain === null || lowestDiastolic < minDomain) {
                minDomain = lowestDiastolic;
            }
        }
        
        if (highestSystolic !== null) {
            if (maxDomain === null || highestSystolic > maxDomain) {
                maxDomain = highestSystolic;
            }
        }

        // Fallback if no limits or data
        if (minDomain === null) minDomain = 0;
        if (maxDomain === null) maxDomain = 200;

        const bufferPercentage = 0.2;
        const range = maxDomain - minDomain;
        const buffer = range * bufferPercentage;
        const newMin = minDomain - buffer < 0 ? minDomain : minDomain - buffer;
        const newMax = maxDomain + buffer;

        return { 
            formatted: [Math.round(newMin), Math.round(newMax)],
            raw: [newMin, newMax]
        };
    }, [systolicUpperLimit, systolicLowerLimit, diastolicUpperLimit, diastolicLowerLimit, highestSystolic, lowestDiastolic]);

    // Generate ticks with more intermediate values within the range
    const ticks = useMemo(() => {
        if (!domain) return [];
        
        // Use raw numeric values from domain for accurate tick calculation
        const minValue = domain.raw[0];
        const maxValue = domain.raw[1];
        
        // Generate evenly spaced ticks (approximately 6 ticks to match grid rows)
        const numTicks = 6;
        const range = maxValue - minValue;
        const step = range / (numTicks - 1);
        
        let tickArray = [minValue, maxValue];
        
        // Add evenly spaced intermediate ticks
        for (let i = 1; i < numTicks - 1; i++) {
            const tickValue = minValue + (step * i);
            tickArray.push(tickValue);
        }
        
        // Add threshold values only if they're within the domain range
        if (systolicLowerLimit !== 0 && systolicLowerLimit !== null && systolicLowerLimit !== undefined) {
            if (systolicLowerLimit >= minValue && systolicLowerLimit <= maxValue) {
                tickArray.push(systolicLowerLimit);
            }
        }
        if (diastolicLowerLimit !== 0 && diastolicLowerLimit !== null && diastolicLowerLimit !== undefined) {
            if (diastolicLowerLimit >= minValue && diastolicLowerLimit <= maxValue) {
                tickArray.push(diastolicLowerLimit);
            }
        }
        if (systolicUpperLimit !== 0 && systolicUpperLimit !== null && systolicUpperLimit !== undefined) {
            if (systolicUpperLimit >= minValue && systolicUpperLimit <= maxValue) {
                tickArray.push(systolicUpperLimit);
            }
        }
        if (diastolicUpperLimit !== 0 && diastolicUpperLimit !== null && diastolicUpperLimit !== undefined) {
            if (diastolicUpperLimit >= minValue && diastolicUpperLimit <= maxValue) {
                tickArray.push(diastolicUpperLimit);
            }
        }

        const uniqueTicks = Array.from(new Set(tickArray.filter(tick => tick !== undefined && tick !== null && !isNaN(tick))))
            .sort((a, b) => b - a)
            .map((item) => Math.round(item));
        
        // Remove duplicates after formatting (in case formatting produces same values)
        return Array.from(new Set(uniqueTicks));
    }, [domain, systolicLowerLimit, diastolicLowerLimit, systolicUpperLimit, diastolicUpperLimit]);

    // Group systolic and diastolic measurements by date/time
    const chartData = useMemo(() => {
        if (!systolicMeasurements || !diastolicMeasurements || 
            (systolicMeasurements.length === 0 && diastolicMeasurements.length === 0)) {
            return { labels: [], systolic: [], diastolic: [], dataPoints: [] };
        }

        // Combine and sort all measurements by time
        const allMeasurements = [
            ...systolicMeasurements.map(m => ({ ...m, type: 'systolic' })),
            ...diastolicMeasurements.map(m => ({ ...m, type: 'diastolic' }))
        ].sort((a, b) => a.sortTime - b.sortTime);

        // Group by date/time
        const groupedByDate = {};
        allMeasurements.forEach(m => {
            const dateKey = m.date || m.groupKey || `${m.formattedDate}-${m.timeStamp}`;
            if (!groupedByDate[dateKey]) {
                groupedByDate[dateKey] = {
                    label: m.date,
                    timeStamp: m.timeStamp,
                    sortTime: m.sortTime,
                    systolic: [],
                    diastolic: []
                };
            }
            if (m.type === 'systolic') {
                groupedByDate[dateKey].systolic.push(m.measurement);
            } else {
                groupedByDate[dateKey].diastolic.push(m.measurement);
            }
        });

        // Extract labels and data (use average if multiple devices at same time)
        const sortedKeys = Object.keys(groupedByDate).sort((a, b) => 
            groupedByDate[a].sortTime - groupedByDate[b].sortTime
        );
        
        const labels = sortedKeys.map(key => groupedByDate[key].label);
        const systolic = sortedKeys.map(key => {
            const values = groupedByDate[key].systolic.filter(v => v !== null && v !== undefined && !isNaN(v));
            if (values.length === 0) return null;
            return values.length > 1 
                ? values.reduce((sum, val) => sum + val, 0) / values.length
                : values[0];
        });
        const diastolic = sortedKeys.map(key => {
            const values = groupedByDate[key].diastolic.filter(v => v !== null && v !== undefined && !isNaN(v));
            if (values.length === 0) return null;
            return values.length > 1 
                ? values.reduce((sum, val) => sum + val, 0) / values.length
                : values[0];
        });

        const dataPoints = sortedKeys.map(key => ({
            ...groupedByDate[key],
            systolic: systolic[sortedKeys.indexOf(key)],
            diastolic: diastolic[sortedKeys.indexOf(key)]
        }));

        return { labels, systolic, diastolic, dataPoints };
    }, [systolicMeasurements, diastolicMeasurements]);

    const labelCount = chartData.labels.length;

    // Create floating bar data: [diastolic, systolic] for each point
    const floatingBarData = useMemo(() => {
        if (!chartData || chartData.labels.length === 0) return [];
        
        return chartData.labels.map((label, index) => {
            const systolic = chartData.systolic[index];
            const diastolic = chartData.diastolic[index];
            
            // Return [start, end] for floating bar, or null if missing data
            if (systolic !== null && diastolic !== null) {
                return [diastolic, systolic];
            }
            return null;
        });
    }, [chartData]);

    // Determine bar colors based on thresholds
    const barColors = useMemo(() => {
        if (!chartData || chartData.labels.length === 0) return [];
        
        return chartData.labels.map((label, index) => {
            const systolic = chartData.systolic[index];
            const diastolic = chartData.diastolic[index];
            
            if (systolic === null || diastolic === null) return '#7A6B78';
            
            // Check if out of range
            if ((systolicUpperLimit !== 0 && systolicUpperLimit !== null && systolic > systolicUpperLimit) ||
                (systolicLowerLimit !== 0 && systolicLowerLimit !== null && systolic < systolicLowerLimit) ||
                (diastolicUpperLimit !== 0 && diastolicUpperLimit !== null && diastolic > diastolicUpperLimit) ||
                (diastolicLowerLimit !== 0 && diastolicLowerLimit !== null && diastolic < diastolicLowerLimit)) {
                return '#EBB6B6'; // Light red for out of range
            }
            
            return '#7A6B78'; // Default gray
        });
    }, [chartData, systolicUpperLimit, systolicLowerLimit, diastolicUpperLimit, diastolicLowerLimit]);

    // Create datasets for floating bars
    const datasets = useMemo(() => {
        if (!chartData || chartData.labels.length === 0) return [];
        
        const result = [];

        // Floating bar dataset
        result.push({
            label: 'Blood Pressure',
            data: floatingBarData,
            backgroundColor: barColors,
            borderColor: barColors.map(color => color === '#EBB6B6' ? '#333' : color),
            borderWidth: 1,
            barThickness: 10,
            order: 3,
            yAxisID: 'y'
        });

        // Reference lines for thresholds (using Line dataset for dashed lines)
        if (systolicUpperLimit !== 0 && systolicUpperLimit !== null && systolicUpperLimit !== undefined && labelCount > 0) {
            result.push({
                type: 'line',
                label: 'Systolic Upper Limit',
                data: Array(labelCount).fill(systolicUpperLimit),
                borderColor: '#CA3B3B',
                backgroundColor: 'transparent',
                borderDash: [3, 3],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                order: 1,
                yAxisID: 'y'
            });
        }

        if (systolicLowerLimit !== 0 && systolicLowerLimit !== null && systolicLowerLimit !== undefined && labelCount > 0) {
            result.push({
                type: 'line',
                label: 'Systolic Lower Limit',
                data: Array(labelCount).fill(systolicLowerLimit),
                borderColor: '#CA3B3B',
                backgroundColor: 'transparent',
                borderDash: [3, 3],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                order: 1,
                yAxisID: 'y'
            });
        }

        if (diastolicUpperLimit !== 0 && diastolicUpperLimit !== null && diastolicUpperLimit !== undefined && labelCount > 0) {
            result.push({
                type: 'line',
                label: 'Diastolic Upper Limit',
                data: Array(labelCount).fill(diastolicUpperLimit),
                borderColor: '#CA3B3B',
                backgroundColor: 'transparent',
                borderDash: [3, 3],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                order: 1,
                yAxisID: 'y'
            });
        }

        if (diastolicLowerLimit !== 0 && diastolicLowerLimit !== null && diastolicLowerLimit !== undefined && labelCount > 0) {
            result.push({
                type: 'line',
                label: 'Diastolic Lower Limit',
                data: Array(labelCount).fill(diastolicLowerLimit),
                borderColor: '#CA3B3B',
                backgroundColor: 'transparent',
                borderDash: [3, 3],
                borderWidth: 1,
                pointRadius: 0,
                fill: false,
                order: 1,
                yAxisID: 'y'
            });
        }

        return result;
    }, [chartData, floatingBarData, barColors, systolicUpperLimit, systolicLowerLimit, diastolicUpperLimit, diastolicLowerLimit, labelCount]);

    // X-axis labels from chart data labels - limit to max 6-8 labels evenly spaced
    const xAxisLabels = useMemo(() => {
        if (!chartData.labels || chartData.labels.length === 0) return [];
        if (!chartData.dataPoints || chartData.dataPoints.length === 0) return [];
        
        // Limit to max 6 labels to prevent overflow
        const maxLabels = 6;
        const totalLabels = chartData.dataPoints.length;
        
        if (totalLabels <= maxLabels) {
            // Show all labels if we have 6 or fewer
            return chartData.dataPoints.map((dataPoint, index) => {
                if (!dataPoint || !dataPoint.timeStamp) {
                    return {
                        time: chartData.labels[index] || '',
                        date: null,
                        fullDate: chartData.labels[index] || '',
                        index: index
                    };
                }
                
                const dateObj = new Date(dataPoint.timeStamp);
                const hour = dateObj.getHours();
                const minute = dateObj.getMinutes();
                const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
                const ampm = hour >= 12 ? 'PM' : 'AM';
                const timeStr = `${hour12}:${String(minute).padStart(2, '0')}${ampm}`;
                
                return {
                    time: timeStr,
                    date: chartData.labels[index] || null,
                    fullDate: chartData.labels[index] || '',
                    index: index
                };
            });
        }
        
        // Show evenly spaced labels (first, last, and evenly distributed in between)
        const step = Math.floor((totalLabels - 1) / (maxLabels - 1));
        const indices = [];
        
        // Always include first and last
        indices.push(0);
        for (let i = step; i < totalLabels - 1; i += step) {
            if (indices.length < maxLabels - 1) {
                indices.push(i);
            }
        }
        if (indices[indices.length - 1] !== totalLabels - 1) {
            indices.push(totalLabels - 1);
        }
        
        return indices.map((index) => {
            const dataPoint = chartData.dataPoints[index];
            if (!dataPoint || !dataPoint.timeStamp) {
                return {
                    time: chartData.labels[index] || '',
                    date: null,
                    fullDate: chartData.labels[index] || '',
                    index: index
                };
            }
            
            const dateObj = new Date(dataPoint.timeStamp);
            const hour = dateObj.getHours();
            const minute = dateObj.getMinutes();
            const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
            const ampm = hour >= 12 ? 'PM' : 'AM';
            const timeStr = `${hour12}:${String(minute).padStart(2, '0')}${ampm}`;
            
            return {
                time: timeStr,
                date: chartData.labels[index] || null,
                fullDate: chartData.labels[index] || '',
                index: index
            };
        });
    }, [chartData]);

    // Early return after all hooks
    if (!vitalConfig || chartData.labels.length === 0) {
        return null;
    }

    const chartDataWithDatasets = {
        labels: chartData.labels,
        datasets: datasets
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false
            },
            tooltip: {
                enabled: true,
                filter: (tooltipItem) => {
                    // Only show tooltip for floating bars, not reference lines
                    return tooltipItem.datasetIndex === 0;
                },
                callbacks: {
                    title: (tooltipItems) => {
                        // Show date/time in tooltip title
                        const tooltipItem = tooltipItems[0];
                        if (!tooltipItem) return '';
                        
                        const dataIndex = tooltipItem.dataIndex;
                        if (xAxisLabels && xAxisLabels[dataIndex]) {
                            const label = xAxisLabels[dataIndex];
                            if (label.date) {
                                return `${label.date} ${label.time}`;
                            }
                            return label.time || label.fullDate || '';
                        }
                        return chartData.labels[dataIndex] || '';
                    },
                    label: (tooltipItem) => {
                        // For floating bars, show the range
                        const dataIndex = tooltipItem.dataIndex;
                        const systolic = chartData.systolic[dataIndex];
                        const diastolic = chartData.diastolic[dataIndex];
                        
                        if (systolic !== null && diastolic !== null) {
                            return [
                                `Systolic: ${Math.round(systolic)} mmHg`,
                                `Diastolic: ${Math.round(diastolic)} mmHg`
                            ];
                        }
                        return [];
                    },
                    afterBody: (tooltipItems) => {
                        // Show combined BP reading
                        const tooltipItem = tooltipItems[0];
                        if (!tooltipItem) return '';
                        
                        const dataIndex = tooltipItem.dataIndex;
                        const systolic = chartData.systolic[dataIndex];
                        const diastolic = chartData.diastolic[dataIndex];
                        
                        if (systolic !== null && diastolic !== null) {
                            return [`${Math.round(systolic)}/${Math.round(diastolic)} mmHg`];
                        }
                        return [];
                    }
                },
                backgroundColor: 'rgba(0, 0, 0, 0.8)',
                titleColor: '#fff',
                bodyColor: '#fff',
                borderColor: '#496ca3',
                borderWidth: 1,
                padding: 10,
                displayColors: true,
                titleFont: {
                    size: 12,
                    weight: 'bold'
                },
                bodyFont: {
                    size: 12
                }
            }
        },
        scales: {
            y: {
                min: domain.raw[0],
                max: domain.raw[1],
                ticks: {
                    stepSize: undefined,
                    display: false
                },
                grid: {
                    display: false
                },
                border: {
                    display: false
                }
            },
            x: {
                ticks: {
                    display: false
                },
                grid: {
                    display: false
                },
                border: {
                    display: false
                }
            }
        },
        elements: {
            bar: {
                borderSkipped: false
            }
        }
    };

    // Y-axis labels from calculated ticks
    const yAxisLabels = ticks;
    // Create grid rows (12 rows total)
    const gridRows = Array.from({ length: 12 }, (_, i) => i);

    return (
        <div style={{display: 'flex', gap: '0', flexDirection: 'column', width: '100%'}}>
            {/* Chart Labels Section */}
            <div className={styles.ChartLabelsSection}>
                <div className={styles.MainLabel}>
                    <p className={styles.VitalNameText}>
                        {vitalConfig.title}
                    </p>
                </div>
            </div>

            <div className={styles.ChartLabelsSection}>
                {vitalConfig.unit && (
                    <p className={styles.VitalUnitText}>
                        {vitalConfig.unit}
                    </p>
                )}
                <div className={styles.ChartFilterContent}>
                    <div className={styles.LegendColor}></div>
                    <div className={styles.LegendText}>
                        <p>{vitalConfig.legend}</p>
                    </div>
                </div>
            </div>

            <div className={styles.ChartContainer}>
                <div className={styles.YAxisLabels}>
                    {yAxisLabels.map((label, index) => (
                        <div key={`y-axis-label-${index}-${label}`} className={styles.YAxisLabel}>
                            <p className={styles.YAxisLabelText}>{label}</p>
                        </div>
                    ))}
                </div>

                <div className={styles.ChartGrid} style={{ position: 'relative' }}>
                    <div className={styles.GraphBoxes}>
                        {gridRows.map((rowIndex) => {
                            const isTopRow = rowIndex === 0;
                            const isBottomRow = rowIndex === gridRows.length - 1;
                            
                            return (
                                <React.Fragment key={rowIndex}>
                                    <div 
                                        className={`${styles.GraphRow} ${
                                            isTopRow ? styles.GraphRowTop : 
                                            isBottomRow ? styles.GraphRowBottom : 
                                            ''
                                        }`}
                                    >
                                        {Array.from({ length: 6 }, (_, colIndex) => {
                                            const isLastCol = colIndex === 5;
    
                                            
                                            return (
                                                <div
                                                    key={colIndex}
                                                    className={`${styles.GraphSquare} ${
                                                        styles.GraphSquareWhite
                                                    } ${isLastCol ? styles.GraphSquareLast : ''}`}
                                                />
                                            );
                                        })}
                                    </div>
                                </React.Fragment>
                            );
                        })}
                    </div>

                    <div style={{ 
                        position: 'absolute', 
                        left: '8px', 
                        top: '0', 
                        width: 'calc(100% - 16px)', 
                        height: '500px',
                        pointerEvents: 'auto'
                    }}>
                        <Bar data={chartDataWithDatasets} options={chartOptions} />
                    </div>

                    <div className={styles.XAxisLabels}>
                        {xAxisLabels.length > 0 ? (
                            xAxisLabels.map((label, index) => (
                                <div key={`x-axis-label-${label.index}-${index}`} className={styles.XAxisLabel}>
                                    <p className={`${styles.XAxisLabelText} ${styles.XAxisLabelTextTop}`}>
                                        {label.time}
                                    </p>
                                    {label.date && (
                                        <p className={`${styles.XAxisLabelText} ${styles.XAxisLabelTextBottom}`}>
                                            {label.date}
                                        </p>
                                    )}
                                </div>
                            ))
                        ) : chartData.labels.length > 0 ? (
                            chartData.labels.map((label, index) => (
                                <div key={index} className={styles.XAxisLabel}>
                                    <p className={styles.XAxisLabelText}>{label}</p>
                                </div>
                            ))
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InpatientBloodPressureChart;

